import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  FareBasisCode,
  FareBasisCodeCreateRequest,
  FareBasisCodeUpdateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getDropdownOptions } from '@/modules/dataParser.js';
import { buildAbstractMap, toOptionalNumber } from './common.ts';

type LegacyFareBasisCode = FareBasisCode & {
  class?: string | null;
};

type LegacyFareBasisCodePayload = Partial<LegacyFareBasisCode> & Record<string, unknown>;

function normalizeFareBasisCode(
  entry: FareBasisCode | null | undefined,
  showOnlyActive?: boolean
): LegacyFareBasisCode | undefined {
  if (!entry) {
    return undefined;
  }

  return {
    ...entry,
    class: entry.classEscaped ?? null,
    isActive: showOnlyActive ?? entry.isActive ?? null,
  };
}

function buildCreateRequest(data: LegacyFareBasisCodePayload): FareBasisCodeCreateRequest {
  const { class: className, classEscaped, tourOperatorId, ...rest } = data;

  return {
    ...rest,
    classEscaped: String(className ?? classEscaped ?? ''),
    tourOperatorId: toOptionalNumber(tourOperatorId),
  } as FareBasisCodeCreateRequest;
}

function buildUpdateRequest(data: LegacyFareBasisCodePayload): FareBasisCodeUpdateRequest {
  const { class: className, classEscaped, tourOperatorId, ...rest } = data;

  return {
    ...rest,
    ...(className !== undefined || classEscaped !== undefined
      ? { classEscaped: String(className ?? classEscaped ?? '') }
      : {}),
    tourOperatorId: toOptionalNumber(tourOperatorId),
  } as FareBasisCodeUpdateRequest;
}

export const useFareBasisCodesStore = defineStore('fareBasisCode', () => {
  const apiClient = useApiClient();

  const list = ref<LegacyFareBasisCode[]>([]);
  const abstract = ref<Record<string, string>>({});
  const added = ref<LegacyFareBasisCode | null>(null);
  const loading = ref<boolean>(true);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function add(data: LegacyFareBasisCodePayload): Promise<LegacyFareBasisCode | undefined> {
    const response = await apiClient.fareBasisCode.post(buildCreateRequest(data));
    added.value = normalizeFareBasisCode(response) ?? null;
    return added.value ?? undefined;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.fareBasisCode.byId(id).byIsActive(active).post();
  }

  async function update(data: LegacyFareBasisCodePayload): Promise<ArrayBuffer | undefined> {
    return await apiClient.fareBasisCode.put(buildUpdateRequest(data));
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<LegacyFareBasisCode[]> {
    loading.value = true;

    try {
      const response = await apiClient.fareBasisCode.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = (response ?? [])
        .map(entry => normalizeFareBasisCode(entry, showOnlyActive))
        .filter((entry): entry is LegacyFareBasisCode => Boolean(entry));
      abstract.value = buildAbstractMap(list.value, entry => entry.code);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    abstract,
    added,
    loading,
    options,
    add,
    toggle,
    update,
    get,
  };
});
